// @figma file=pkCGby54zXzTsccXvHqzO6 node=3868:82886 name="✦ Slider" set="mytaible-shadcn-studio" verified=2026-03-21

import * as SliderPrimitive from '@radix-ui/react-slider';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Root-level CVA — drives orientation layout and the `--slider-color` CSS
 * custom property that is shared by the Range fill and Thumb border.
 *
 * Figma dimensions mapped:
 *   Colors   → variant      (Primary | Info | Success | Warning | Error)
 *   Variants → orientation  (Horizontal | vertical — vertical is code-only)
 */
const sliderVariants = cva('relative flex touch-none select-none', {
  variants: {
    /**
     * Layout axis.
     * Figma: Variants=Horizontal (default). Vertical is implemented via
     * Radix orientation prop; no dedicated Figma frame exists.
     */
    orientation: {
      horizontal: 'w-full items-center',
      vertical: 'h-full flex-col justify-center',
    },
    /**
     * Colour intent — Figma: Colors dimension.
     * Sets --slider-color which drives both Range fill + Thumb border.
     */
    variant: {
      primary: '[--slider-color:var(--color-primary)]',
      info: '[--slider-color:var(--color-info)]',
      success: '[--slider-color:var(--color-success)]',
      warning: '[--slider-color:var(--color-warning)]',
      destructive: '[--slider-color:var(--color-destructive)]',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
    variant: 'primary',
  },
});

// ─── Size helpers ──────────────────────────────────────────────────────────────

/**
 * Track dimension by orientation + size.
 * Horizontal: height is fixed, width fills the container.
 * Vertical:   width is fixed,  height fills the container.
 * Figma: size = sm (4 px) | md (6 px, default) | lg (8 px).
 */
const trackSizeClass = {
  horizontal: { sm: 'h-1', md: 'h-1.5', lg: 'h-2' },
  vertical: { sm: 'w-1', md: 'w-1.5', lg: 'w-2' },
} as const;

/**
 * Thumb dimension by size.
 * Figma: sm = 16 px | md = 20 px | lg = 24 px.
 */
const thumbSizeClass = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SliderProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>, 'orientation'>,
    VariantProps<typeof sliderVariants> {
  /**
   * Controlled value array.
   * One element → single-thumb.  Two elements → range (double-thumb).
   * Figma: Style=Half-Full (single) | Style=Double slider (range).
   */
  value?: number[];
  /** Uncontrolled initial value. Same shape as `value`. */
  defaultValue?: number[];
  /** Callback fired on every change with the current value array. */
  onValueChange?: (value: number[]) => void;
  /** Minimum selectable value. Default: 0. */
  min?: number;
  /** Maximum selectable value. Default: 100. */
  max?: number;
  /** Step size between selectable values. Default: 1. */
  step?: number;
  /**
   * When true the slider is non-interactive and visually muted.
   * Figma: disabled state — opacity-50, pointer-events-none on thumb.
   */
  disabled?: boolean;
  /**
   * Slider axis.
   * Figma: Variants=Horizontal (default). Vertical is code-only.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Track + thumb scale.
   * Figma: size = sm | md | lg.
   */
  size?: 'sm' | 'md' | 'lg';
  /** Additional classes merged onto the root element. */
  className?: string;
  /**
   * Accessible label forwarded to each Thumb element (the interactive
   * `role="slider"` node produced by Radix).
   *
   * Use this so assistive technologies — and `getByRole('slider', { name })`
   * in tests — can identify the widget.  For a range slider both thumbs
   * receive the same label; supply individual `aria-label` overrides directly
   * on each thumb if you need distinct names in a range context.
   *
   * Note: `aria-label` placed on `<Slider>` is forwarded to the Root
   * container element (group context) — not to the Thumb.  Use `thumbLabel`
   * to label the interactive thumb.
   *
   * @example
   * <Slider defaultValue={[50]} thumbLabel="Volume" />
   */
  thumbLabel?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Numeric range input built on Radix `SliderPrimitive`.
 *
 * Supports single-thumb and range (double-thumb) modes, horizontal and
 * vertical orientation, and five colour variants matching the Figma
 * Colors dimension (Primary / Info / Success / Warning / Error).
 *
 * Keyboard accessible out of the box: Arrow keys move the focused thumb;
 * Home / End jump to min / max. ARIA `role="slider"` is set automatically
 * by Radix on each thumb.
 *
 * **Accessible name**: Pass `thumbLabel` to label each Thumb (`role="slider"`).
 * The root container accepts `aria-label` / `aria-labelledby` for group context.
 *
 * @example
 * // Single-thumb — uncontrolled
 * <Slider defaultValue={[50]} thumbLabel="Volume" />
 *
 * @example
 * // Range — controlled
 * const [range, setRange] = React.useState([20, 80]);
 * <Slider value={range} onValueChange={setRange} thumbLabel="Price range" />
 *
 * @example
 * // Vertical — consumer must supply a height
 * <Slider orientation="vertical" defaultValue={[40]} className="h-48" thumbLabel="Level" />
 *
 * @example
 * // Disabled
 * <Slider defaultValue={[30]} disabled thumbLabel="Volume" />
 */
const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      variant = 'primary',
      orientation = 'horizontal',
      size = 'md',
      value,
      defaultValue,
      // thumbLabel is forwarded as aria-label to each SliderPrimitive.Thumb.
      // Radix places role="slider" on the Thumb — not on the Root — so the
      // accessible name must live there for AT / testing-library to find it.
      thumbLabel,
      ...props
    },
    ref,
  ) => {
    const isVertical = orientation === 'vertical';

    // Number of thumbs derives from the controlled value or uncontrolled
    // defaultValue length.  Falls back to 1 (single-thumb) when neither
    // is provided — Radix will default to position 0 / min.
    const thumbCount = value?.length ?? defaultValue?.length ?? 1;

    return (
      <SliderPrimitive.Root
        ref={ref}
        orientation={orientation}
        value={value}
        defaultValue={defaultValue}
        className={cn(sliderVariants({ variant, orientation }), className)}
        {...props}
      >
        {/* ── Track ───────────────────────────────────────────────────── */}
        <SliderPrimitive.Track
          className={cn(
            'relative grow overflow-hidden rounded-full bg-muted',
            // Figma: track height (h) for horizontal, track width (w) for vertical
            trackSizeClass[orientation][size],
          )}
        >
          {/* ── Range fill — colour via --slider-color token ───────────── */}
          <SliderPrimitive.Range
            className={cn(
              'absolute rounded-full bg-[var(--slider-color)]',
              // In horizontal mode fill the height; in vertical fill the width
              isVertical ? 'w-full' : 'h-full',
            )}
          />
        </SliderPrimitive.Track>

        {/* ── Thumb(s) ─────────────────────────────────────────────────── */}
        {/* Render one thumb per value element (1 = single, 2 = range).   */}
        {/* thumbLabel is forwarded as aria-label — Radix places           */}
        {/* role="slider" here, so the accessible name must live here.     */}
        {Array.from({ length: thumbCount }).map((_, i) => (
          <SliderPrimitive.Thumb
            key={i}
            aria-label={thumbLabel}
            className={cn(
              // ── Shape (Figma: circular thumb with border) ────────────
              'block rounded-full border-2 bg-background',
              thumbSizeClass[size],
              // ── Border inherits --slider-color ───────────────────────
              'border-[var(--slider-color)]',
              // ── Focus ring ───────────────────────────────────────────
              'ring-offset-background transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              // ── Disabled — Figma: opacity-50, no pointer events ──────
              'disabled:pointer-events-none disabled:opacity-50',
            )}
          />
        ))}
      </SliderPrimitive.Root>
    );
  },
);
Slider.displayName = 'Slider';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Slider, sliderVariants };
